import { useMemo, useState } from 'react';
import { ArrowLeft, Check, Clock, X } from 'lucide-react';
import { BoutonMint, BoutonSecondaire, Carte, MessageErreur } from '../ui/Primitives';
import type { PresenceAffichee, SeanceAffichee } from '../../lib/useEmargement';

/**
 * Étape de l'appel, avant toute signature.
 *
 * Elle existe pour une raison pratique : on ne fait pas circuler une tablette
 * auprès de chaises vides. Le formateur pointe la salle d'abord, valide, et
 * seuls les présents passent ensuite à la signature.
 *
 * Trois états seulement, en grands boutons : présent, en retard, absent. Le
 * motif de justification n'apparaît que sur une absence, l'heure d'arrivée que
 * sur un retard — les demander plus tôt ralentirait l'appel, qui se fait
 * debout, devant le groupe. L'heure reste facultative : elle se complète après
 * coup depuis le détail de l'apprenant.
 *
 * Le code de sortie du mode signature n'est demandé que faute de code
 * paramétré sur la session ou la formation : celui-là s'applique d'office.
 */

export type StatutAppel = 'PRESENT' | 'RETARD' | 'ABSENT';

export interface PointageLocal {
  statut: StatutAppel;
  heureArrivee: string;
  justifiee: boolean;
  motifJustification: string;
}

/**
 * Pointage de départ d'une ligne.
 *
 * Une ligne déjà signée est présente, et le reste : on ne repropose pas de la
 * déclarer absente dans l'écran d'appel, alors qu'une signature a été recueillie.
 * Une ligne encore attendue est proposée présente — c'est le cas le plus
 * fréquent, et l'appel consiste à en retirer les manquants.
 */
function pointageInitial(presence: PresenceAffichee): PointageLocal {
  return {
    statut:
      presence.statut === 'ABSENT'
        ? 'ABSENT'
        : presence.statut === 'RETARD'
          ? 'RETARD'
          : 'PRESENT',
    heureArrivee: presence.heureArrivee ?? '',
    justifiee: presence.justifiee,
    motifJustification: presence.motifJustification ?? '',
  };
}

const CHOIX: { statut: StatutAppel; libelle: string; icone: typeof Check; ton: string }[] = [
  { statut: 'PRESENT', libelle: 'Présent', icone: Check, ton: 'bg-codialis-mint text-codialis-navy' },
  { statut: 'RETARD', libelle: 'Retard', icone: Clock, ton: 'bg-amber-400 text-codialis-navy' },
  { statut: 'ABSENT', libelle: 'Absent', icone: X, ton: 'bg-red-500 text-white' },
];

export function AppelSession({
  seance,
  enCours,
  erreur,
  onRetour,
  onValider,
}: {
  seance: SeanceAffichee;
  enCours: boolean;
  erreur: string | null;
  onRetour: () => void;
  onValider: (
    pointages: {
      presenceId: string;
      statut: StatutAppel;
      heureArrivee?: string;
      justifiee?: boolean;
      motifJustification?: string;
    }[],
    pin: string | undefined,
  ) => void;
}): JSX.Element {
  const [pointages, setPointages] = useState<Record<string, PointageLocal>>(() =>
    Object.fromEntries(
      seance.presences.map((presence) => [presence.id, pointageInitial(presence)]),
    ),
  );
  const [pin, setPin] = useState('');
  const [erreurPin, setErreurPin] = useState<string | null>(null);

  const compteurs = useMemo(() => {
    const valeurs = Object.values(pointages);
    return {
      presents: valeurs.filter((valeur) => valeur.statut === 'PRESENT').length,
      retards: valeurs.filter((valeur) => valeur.statut === 'RETARD').length,
      absents: valeurs.filter((valeur) => valeur.statut === 'ABSENT').length,
    };
  }, [pointages]);

  function changer(presenceId: string, modification: Partial<PointageLocal>): void {
    setPointages((actuels) => ({
      ...actuels,
      [presenceId]: { ...actuels[presenceId]!, ...modification },
    }));
  }

  const codeParametre = seance.codeSource === 'SESSION' || seance.codeSource === 'FORMATION';

  function valider(): void {
    if (!codeParametre && !/^\d{4}$/.test(pin)) {
      setErreurPin(
        'Choisissez un code à quatre chiffres : il servira à quitter le mode signature ' +
          'une fois la tablette revenue.',
      );
      return;
    }
    setErreurPin(null);
    onValider(
      seance.presences.map((presence) => {
        const pointage = pointages[presence.id]!;
        return {
          presenceId: presence.id,
          statut: pointage.statut,
          ...(pointage.statut === 'RETARD' && pointage.heureArrivee
            ? { heureArrivee: pointage.heureArrivee }
            : {}),
          ...(pointage.statut === 'ABSENT'
            ? {
                justifiee: pointage.justifiee,
                motifJustification: pointage.motifJustification || undefined,
              }
            : {}),
        };
      }),
      codeParametre ? undefined : pin,
    );
  }

  return (
    <div className="space-y-4">
      <Carte
        titre={`Appel · ${seance.demiJourneeLibelle} · ${seance.libelle}`}
        actions={
          <BoutonSecondaire onClick={onRetour}>
            <ArrowLeft className="mr-1.5 h-4 w-4" aria-hidden="true" />
            Changer de session
          </BoutonSecondaire>
        }
        sansBordure
      >
        <div className="space-y-4 p-4 sm:p-6">
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-codialis-gray">
            <span>
              {seance.heureDebut} – {seance.heureFin}
            </span>
            {seance.session && !seance.libelle.includes(seance.session) && (
              <span>{seance.session}</span>
            )}
            <span className="font-semibold text-codialis-navy">
              {compteurs.presents} présent{compteurs.presents > 1 ? 's' : ''} ·{' '}
              {compteurs.retards} en retard · {compteurs.absents} absent
              {compteurs.absents > 1 ? 's' : ''}
            </span>
          </div>

          <ul className="space-y-2">
            {seance.presences.map((presence) => {
              const pointage = pointages[presence.id]!;
              return (
                <li
                  key={presence.id}
                  className="rounded-2xl border border-gray-100 bg-white p-3 sm:p-4"
                >
                  <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-codialis-navy">
                        {presence.apprenant}
                      </p>
                      {presence.signeLe && (
                        <p className="text-xs text-codialis-gray">
                          A déjà signé cette demi-journée
                        </p>
                      )}
                    </div>

                    <div
                      className="flex gap-2"
                      role="group"
                      aria-label={`État de ${presence.apprenant}`}
                    >
                      {CHOIX.map((choix) => {
                        const actif = pointage.statut === choix.statut;
                        const Icone = choix.icone;
                        return (
                          <button
                            key={choix.statut}
                            type="button"
                            aria-pressed={actif}
                            onClick={() => changer(presence.id, { statut: choix.statut })}
                            className={`inline-flex min-h-[44px] flex-1 items-center justify-center gap-1.5 rounded-xl px-3 text-sm font-semibold transition [@media(pointer:coarse)]:min-h-[48px] md:flex-none ${
                              actif
                                ? choix.ton
                                : 'border border-gray-200 bg-white text-codialis-gray hover:bg-gray-50'
                            }`}
                          >
                            <Icone className="h-4 w-4" aria-hidden="true" />
                            {choix.libelle}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {pointage.statut === 'RETARD' && (
                    <div className="mt-3 flex flex-col gap-2 border-t border-gray-100 pt-3 sm:flex-row sm:items-center">
                      <label
                        htmlFor={`arrivee-${presence.id}`}
                        className="text-sm text-codialis-navy sm:shrink-0"
                      >
                        Arrivée à
                      </label>
                      <input
                        id={`arrivee-${presence.id}`}
                        type="time"
                        value={pointage.heureArrivee}
                        min={seance.heureDebut}
                        max={seance.heureFin}
                        onChange={(evenement) =>
                          changer(presence.id, { heureArrivee: evenement.target.value })
                        }
                        className="min-h-[44px] w-full min-w-0 rounded-xl border border-gray-200 px-3 text-sm sm:w-40"
                      />
                      <span className="text-xs text-codialis-gray">
                        Facultatif — se complète ensuite depuis le détail.
                      </span>
                    </div>
                  )}

                  {pointage.statut === 'ABSENT' && (
                    <div className="mt-3 flex flex-col gap-2 border-t border-gray-100 pt-3 sm:flex-row sm:items-center">
                      <label className="inline-flex min-h-[44px] items-center gap-2 text-sm text-codialis-navy">
                        <input
                          type="checkbox"
                          className="h-5 w-5 rounded"
                          checked={pointage.justifiee}
                          onChange={(evenement) =>
                            changer(presence.id, { justifiee: evenement.target.checked })
                          }
                        />
                        Absence justifiée
                      </label>
                      <input
                        type="text"
                        aria-label="Motif de la justification"
                        value={pointage.motifJustification}
                        onChange={(evenement) =>
                          changer(presence.id, { motifJustification: evenement.target.value })
                        }
                        placeholder="Motif (certificat médical, convocation…)"
                        className="min-h-[44px] min-w-0 flex-1 rounded-xl border border-gray-200 px-3 text-sm"
                      />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </Carte>

      <Carte titre="Code de sortie du mode signature" sansBordure>
        <div className="space-y-3 p-4 sm:p-6">
          {codeParametre ? (
            <p className="text-sm text-codialis-gray">
              La tablette va circuler dans la salle. Le {seance.codeSourceLibelle} sera demandé
              pour quitter le mode signature : il est paramétré, rien à choisir ici.
            </p>
          ) : (
            <>
              <p className="text-sm text-codialis-gray">
                La tablette va circuler dans la salle. Aucun code n’est paramétré pour cette
                session ni sa formation : choisissez-en un à quatre chiffres, il sera demandé
                pour quitter le mode signature.
              </p>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="off"
                maxLength={4}
                value={pin}
                onChange={(evenement) => setPin(evenement.target.value.replace(/\D/g, ''))}
                placeholder="0000"
                aria-label="Code de sortie à quatre chiffres"
                className="min-h-[56px] w-40 rounded-xl border-2 border-gray-200 text-center font-display text-2xl tracking-[0.5em] text-codialis-navy"
              />
            </>
          )}
          {erreurPin && <MessageErreur message={erreurPin} />}
          {erreur && <MessageErreur message={erreur} />}

          <div className="flex flex-wrap gap-2">
            <BoutonMint onClick={valider} disabled={enCours}>
              {enCours ? 'Validation…' : 'Valider l’appel et passer aux signatures'}
            </BoutonMint>
          </div>
        </div>
      </Carte>
    </div>
  );
}
